import { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown, LucideIcon } from "lucide-react";
import {
    ShieldCheck,
    Lock,
    Globe,
    Wifi,
    Server,
    Activity,
    Rocket,
} from "lucide-react";
import { useTranslation } from "react-i18next";

type LangCode = "en" | "es" | "it";

function RegionLanguageMenu({
    value,
    onChange,
}: {
    value: LangCode;
    onChange: (lang: LangCode) => void;
}) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const options: Array<{ label: string; code: LangCode }> = [
        { label: "English", code: "en" },
        { label: "Spanish", code: "es" },
        { label: "Italian", code: "it" },
    ];

    const current = options.find((o) => o.code === value) ?? options[0];

    useEffect(() => {
        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    return (
        <div className="relative">
            <button
                ref={btnRef}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {current.label}
                <ChevronDown
                    size={14}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div
                    ref={panelRef}
                    className="absolute right-0 mt-2 w-56 rounded-md border bg-white shadow-xl ring-1 ring-black/5 z-[10000]"
                >
                    {options.map((o) => (
                        <button
                            key={o.code}
                            onClick={() => {
                                onChange(o.code);
                                setOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${value === o.code ? "bg-slate-100 font-medium" : ""
                                }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const MENU = [
    {
        label: "Features",
        children: [
            { label: "Private Access", to: "/solutions/secureidvpn/private-access" },
            { label: "Device Lockdown", to: "/solutions/secureidvpn/device-control" },
            { label: "App-Only Routing", to: "/solutions/secureidvpn/app-routing" },
            { label: "Audit & Logs", to: "/solutions/secureidvpn/audit" },
            { label: "Performance", to: "/solutions/secureidvpn/performance" },
        ],
    },
    {
        label: "Services",
        children: [
            { label: "Managed Deployment", to: "/services/vpn-deployment" },
            { label: "Security Hardening", to: "/services/security" },
        ],
    },
    {
        label: "Resources",
        children: [
            { label: "Help Center", to: "/resources/help" },
            { label: "Training", to: "/resources/training" },
            { label: "FAQ", to: "/resources/faq" },
        ],
    },
    {
        label: "About",
        children: [
            { label: "Why SecureID", to: "/about/why-secureid" },
            { label: "Contact Us", to: "/solutions/secureidvpn/support" },
        ],
    },
];

interface Feature {
    icon: LucideIcon;
    title: string;
    desc: string;
}

function VpnPortal() {
    return (
        <section className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
                <Link
                    to="/secureidvpn/login"
                    className="w-full sm:w-auto text-center bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-indigo-700 transition"
                >
                    🔐 VPN Portal Login
                </Link>
                <Link
                    to="/contact"
                    className="w-full sm:w-auto text-center bg-emerald-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-emerald-700 transition"
                >
                    🧑‍💼 Schedule Setup Call
                </Link>
            </div>
        </section>
    );
}

export default function SecureIDVPN() {
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { i18n } = useTranslation();

    useEffect(() => {
        const closeMenus = () => setOpenMenu(null);
        document.addEventListener("click", closeMenus);
        return () => document.removeEventListener("click", closeMenus);
    }, []);

    const features: Feature[] = [
        {
            icon: Globe,
            title: "Private Network Access",
            desc: "Restrict access so your apps and portals only work from SecureIDVPN.",
        },
        {
            icon: Lock,
            title: "Zero-Trust Controls",
            desc: "Gate sensitive operations behind VPN + auth policies and device rules.",
        },
        {
            icon: Wifi,
            title: "Reliable Connectivity",
            desc: "Optimized tunnels and connection logic for mobile and field devices.",
        },
        {
            icon: Server,
            title: "VPS + Managed Options",
            desc: "Run on your VPS or let SecureID manage deployment and monitoring.",
        },
        {
            icon: Activity,
            title: "Audit & Observability",
            desc: "Connection events, endpoints, and usage logs for compliance and ops.",
        },
        {
            icon: ShieldCheck,
            title: "Security by Design",
            desc: "Encrypted traffic, role-based access, and hardened network policies.",
        },
    ];

    return (
        <main className="bg-white text-gray-900">
            <header className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <img
                            src="/Images/SecureIDVPN.png"
                            alt="SecureIDVPN Logo"
                            className="w-[42px] h-auto rounded-lg shadow border border-gray-200"
                        />
                        <span className="text-xl font-bold text-gray-900">
                            SecureID <span className="text-indigo-600">VPN</span>
                        </span>
                    </div>

                    <nav
                        className="hidden md:flex items-center space-x-8 font-medium text-gray-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-4 border-l pl-6 ml-4 border-gray-200">
                            <Link to="/secureidvpn/login" className="hover:text-indigo-600">
                                VPN Portal Login
                            </Link>
                            <Link to="/solutions/secureidvpn/support" className="hover:text-indigo-600">
                                Customer Support
                            </Link>
                            <Link to="/contact" className="hover:text-indigo-600">
                                Schedule a Demo
                            </Link>
                        </div>

                        {["features", "services", "resources", "about"].map((menu) => (
                            <div key={menu} className="relative">
                                <button
                                    onClick={() => setOpenMenu(openMenu === menu ? null : menu)}
                                    className="flex items-center gap-1 hover:text-indigo-600 capitalize"
                                >
                                    {menu}
                                    <svg
                                        className="w-4 h-4 ml-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </button>

                                {openMenu === menu && (
                                    <div className="absolute left-0 mt-2 bg-white border border-gray-100 rounded-lg shadow-lg w-56 z-50">
                                        <ul className="py-2 text-sm text-gray-700">
                                            {(menu === "features" ? MENU[0].children
                                                : menu === "services" ? MENU[1].children
                                                    : menu === "resources" ? MENU[2].children
                                                        : MENU[3].children
                                            ).map((child) => (
                                                <li key={child.label}>
                                                    <Link
                                                        to={child.to}
                                                        className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                    >
                                                        {child.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>

                    <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-gray-700">
                        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                <VpnPortal />

                {mobileOpen && (
                    <div className="md:hidden bg-white border-t border-gray-200 shadow-inner max-h-[85vh] overflow-y-auto px-6 py-4 space-y-4">
                        {MENU.map((item) => (
                            <div key={item.label}>
                                <div className="text-[15px] font-semibold text-gray-900 mb-2">{item.label}</div>
                                <ul className="pl-3 mt-2 space-y-1 bg-indigo-50 rounded-md border border-indigo-100">
                                    {item.children.map((child) => (
                                        <li key={child.label}>
                                            <NavLink
                                                to={child.to}
                                                onClick={() => setMobileOpen(false)}
                                                className={({ isActive }) =>
                                                    [
                                                        "block w-full rounded-md px-3 py-2 text-[15px] transition font-medium !text-indigo-900",
                                                        isActive
                                                            ? "!bg-indigo-100 !text-indigo-800 font-semibold"
                                                            : "hover:!bg-indigo-100 hover:!text-indigo-700",
                                                    ].join(" ")
                                                }
                                            >
                                                {child.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        <div className="pt-4 border-t border-gray-200">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Utility</p>
                            <div className="flex flex-col space-y-2 text-indigo-700 font-medium">
                                <Link to="/secureidvpn/login" onClick={() => setMobileOpen(false)}>
                                    VPN Portal Login
                                </Link>
                                <Link to="/contact" onClick={() => setMobileOpen(false)}>
                                    Schedule a Demo
                                </Link>
                                <Link to="/solutions/secureidvpn/support" onClick={() => setMobileOpen(false)}>
                                    Customer Support
                                </Link>
                            </div>
                        </div>

                        <div className="pt-3 border-t mt-2 space-y-3 !text-indigo-900">
                            <div className="px-2">
                                <RegionLanguageMenu
                                    value={(i18n.language as LangCode) || "en"}
                                    onChange={(lng) => {
                                        i18n.changeLanguage(lng);
                                        setMobileOpen(false);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Hero */}
            <section className="bg-gradient-to-br from-indigo-700 via-indigo-800 to-indigo-900 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-indigo-600/30 border border-indigo-400/40 text-indigo-100 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                            <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                            Private-by-Design Networking
                        </div>

                        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
                            Make Your Apps Work <span className="text-indigo-300">Only on SecureIDVPN</span>
                        </h1>
                        <p className="mt-6 text-lg text-indigo-100">
                            Enforce private access for ExamsPro, Extracta, DactylScan, and internal portals—so sensitive operations
                            happen only through your trusted network.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                📅 Schedule a Demo
                            </Link>

                            <Link
                                to="/pricing"
                                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                View Pricing
                            </Link>

                            <Link
                                to="/solutions/secureidvpn/private-access"
                                className="flex items-center gap-2 bg-indigo-700 hover:bg-indigo-600 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                <Rocket className="w-4 h-4" />
                                Get Started
                            </Link>
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative flex justify-center items-center"
                    >
                        <motion.div
                            animate={{ y: [0, -6, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="relative"
                        >
                            <div className="relative bg-gradient-to-tr from-emerald-400 via-indigo-500 to-purple-600 p-1 rounded-[2.5rem] shadow-2xl">
                                <div className="relative rounded-[2.2rem] bg-white/5 backdrop-blur overflow-hidden border border-white/10">
                                    <div className="p-4">
                                        <div className="rounded-2xl overflow-hidden shadow-xl bg-gray-100">
                                            <img
                                                src="/Images/secureidvpn-hero.jpg"
                                                alt="SecureIDVPN"
                                                className="w-full h-auto object-cover"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Feature Cards */}
            <section className="bg-gray-50 py-20 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-14">
                        SecureIDVPN Gives You Everything You Need To…
                    </h2>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
                        {features.map((f) => (
                            <div
                                key={f.title}
                                className="bg-white rounded-2xl shadow-sm hover:shadow-md p-8 border border-gray-100 transition"
                            >
                                <div className="flex justify-center mb-4">
                                    <div className="p-3 rounded-full bg-indigo-50">
                                        <f.icon className="w-10 h-10 text-indigo-600" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{f.title}</h3>
                                <p className="text-gray-600 mb-6">{f.desc}</p>
                                <Link
                                    to="/solutions/secureidvpn/private-access"
                                    className="inline-block bg-indigo-600 text-white px-6 py-2 rounded-full hover:bg-indigo-700 transition"
                                >
                                    Learn More
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer (same as your style) */}
            <footer className="bg-gray-900 text-gray-300 py-16">
                <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 md:grid-cols-4 gap-10">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-3">SecureID Technologies</h3>
                        <p className="text-sm mb-4">
                            SecureIDVPN — Private network access for SecureID products and enterprise workflows.
                        </p>
                    </div>

                    <div>
                        <h4 className="font-semibold text-white mb-3">Company</h4>
                        <ul className="space-y-1 text-sm">
                            <li><Link to="/about">About SecureID</Link></li>
                            <li><Link to="/careers">Careers</Link></li>
                            <li><Link to="/contact">Contact Us</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-white mb-3">Resources</h4>
                        <ul className="space-y-1 text-sm">
                            <li><Link to="/help">Help Center</Link></li>
                            <li><Link to="/docs">Documentation</Link></li>
                            <li><Link to="/privacy">Privacy & Security</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-white mb-3">Products</h4>
                        <ul className="space-y-1 text-sm">
                            <li><Link to="/solutions/examspro">ExamsPro</Link></li>
                            <li><Link to="/solutions/extracta">Extracta</Link></li>
                            <li><Link to="/solutions/dactylscan">DactylScan</Link></li>
                            <li><Link to="/solutions/secureidvpn">SecureIDVPN</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-gray-700 mt-10 pt-6 text-center text-sm">
                    <p>
                        © 2025 SecureID Technologies LLC — All Rights Reserved.{" "}
                        <Link to="/legal" className="text-indigo-400 hover:underline mx-1">Legal Stuff</Link> |{" "}
                        <Link to="/privacy" className="text-indigo-400 hover:underline mx-1">Privacy Policy</Link>
                    </p>
                </div>
            </footer>
        </main>
    );
}
